/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import AppModalShell, {
  AppModalButton,
  AppModalContentItem,
} from "../common/AppModalShell";

export default function ChatMessageAttentionModal({
  visible = false,
  usedCount = 0,
  limit = 2,
  selectedPreview = "",
  selectedAuthor = "",
  selectedMeta = null,
  desktopMountNode = null,
  canSelect = true,
  showLimit = true,
  copyKey = "host_attention",
  submitting = false,
  showConfirmWhenUnselected = true,
  onClose = () => {},
  onStartSelecting = () => {},
  onConfirm = () => {},
}) {
  const { t } = useTranslation("menu");

  const safeUsedCount = Number.isFinite(Number(usedCount)) ? Number(usedCount) : 0;
  const safeLimit = Number.isFinite(Number(limit)) ? Number(limit) : 2;
  const hasSelectedMessage = !!String(selectedPreview || "").trim();
  const safeSelectedAuthor = String(selectedMeta?.authorLabel || selectedAuthor || "").trim();
  const safeSelectedTime = String(selectedMeta?.timeLabel || "").trim();
  const copyBase = `chats.event_chat.${copyKey}`;

  return (
    <AppModalShell
      open={visible}
      title={t(`${copyBase}.title`)}
      description={t(`${copyBase}.description`)}
      desktopMountNode={desktopMountNode}
      onClose={onClose}
      closeAriaLabel={t(`${copyBase}.close_aria`)}
      maxWidthClassName="max-w-[35rem]"
      panelClassName="border-white/64"
      bodyClassName="px-5 pb-5"
    >
      {showLimit ? (
        <AppModalContentItem className="mb-4 rounded-[22px] border border-white/10 bg-white/[0.07] px-4 py-3 text-center text-[14px] font-semibold leading-5 text-white/82 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
          {t(`${copyBase}.limit`, {
            used: safeUsedCount,
            limit: safeLimit,
          })}
        </AppModalContentItem>
      ) : null}

      {showLimit && !canSelect ? (
        <AppModalContentItem className="mb-4 rounded-[22px] border border-white/10 bg-white/[0.07] px-4 py-3 text-center text-[14px] font-semibold leading-5 text-white/82">
          {t(`${copyBase}.limit_reached`)}
        </AppModalContentItem>
      ) : null}

      {hasSelectedMessage ? (
        <AppModalContentItem className="rounded-[24px] border border-white/10 bg-white/[0.07] px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
          <h3 className="text-center text-[16px] font-bold leading-5 text-white">
            {t(`${copyBase}.selected_label`)}
          </h3>
          <div className="mt-3 rounded-[22px] bg-white/[0.08] px-4 py-3 text-left">
            <div className="mb-2 flex min-w-0 items-start justify-between gap-3">
              {safeSelectedAuthor ? (
                <span className="min-w-0 flex-1 truncate text-[14px] font-extrabold leading-5 text-white">
                  {safeSelectedAuthor}
                </span>
              ) : (
                <span className="min-w-0 flex-1" aria-hidden="true" />
              )}
              {safeSelectedTime ? (
                <span className="shrink-0 text-[13px] font-light leading-5 text-white/70">
                  {safeSelectedTime}
                </span>
              ) : null}
            </div>
            <div className="whitespace-pre-wrap break-words text-[15px] font-medium leading-6 text-white/88">
              {selectedPreview}
            </div>
          </div>
        </AppModalContentItem>
      ) : null}

      <AppModalContentItem className={`${hasSelectedMessage ? "mt-5" : "mt-1"} grid grid-cols-1 gap-3`}>
        {canSelect ? (
          <AppModalButton
            variant="secondary"
            onClick={onStartSelecting}
            disabled={submitting}
            loading={submitting}
          >
            {hasSelectedMessage
              ? t(`${copyBase}.change_message`)
              : t(`${copyBase}.select_message`)}
          </AppModalButton>
        ) : null}

        {showConfirmWhenUnselected || hasSelectedMessage ? (
          <AppModalButton
            variant="primary"
            onClick={onConfirm}
            disabled={!canSelect || !hasSelectedMessage || submitting}
            loading={submitting}
          >
            {submitting
              ? t(`${copyBase}.confirm_loading`)
              : t(`${copyBase}.confirm`)}
          </AppModalButton>
        ) : null}
      </AppModalContentItem>
    </AppModalShell>
  );
}
